import axios from 'axios';
const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('cpr_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      // Don't redirect if it's a blob request (file download) or the public signing page
      const onPublicSign = typeof window !== 'undefined' && /^\/(sign|visit)\//.test(window.location.pathname);
      if (err.config?.responseType !== 'blob' && !onPublicSign) {
        localStorage.removeItem('cpr_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);
// Jobs
export const getBoard = () => api.get('/board').then(r => r.data);
export const updateBoard = (data) => api.put('/board', data).then(r => r.data);
export const getBoardHistory = () => api.get('/board/history').then(r => r.data);
// Backfill / correct a specific day without touching the live board row.
export const getBoardDay = (date) => api.get(`/board/history/${date}`).then(r => r.data);
export const saveBoardDay = (date, data) => api.put(`/board/history/${date}`, data).then(r => r.data);
export const deleteBoardDay = (date) => api.delete(`/board/history/${date}`).then(r => r.data);
export const getMissingBoardDays = (from, to) =>
  api.get('/board/history/missing', { params: { from, to } }).then(r => r.data);
export const getJobs = (params) => api.get('/jobs', { params }).then(r => r.data);
export const getJob = (id) => api.get(`/jobs/${id}`).then(r => r.data);
export const getJobByPO = (po) => api.get(`/jobs/po/${encodeURIComponent(po)}`).then(r => r.data);
export const getJobStats = () => api.get('/jobs/stats').then(r => r.data);
export const createJob = (data) => api.post('/jobs', data).then(r => r.data);
export const updateJob = (id, data) => api.put(`/jobs/${id}`, data).then(r => r.data);
export const deleteJob = (id) => api.delete(`/jobs/${id}`).then(r => r.data);
export const getRecycleBin = () => api.get('/jobs/recycle-bin').then(r => r.data);
export const restoreJob = (id) => api.post(`/jobs/${id}/restore`).then(r => r.data);
export const purgeJob = (id) => api.delete(`/jobs/${id}/purge`).then(r => r.data);
export const getAuditLog = (limit = 200) => api.get('/jobs/audit-log', { params: { limit } }).then(r => r.data);
// Staff
export const getStaff = () => api.get('/staff').then(r => r.data);
export const createStaff = (data) => api.post('/staff', data).then(r => r.data);
export const updateStaff = (id, data) => api.put(`/staff/${id}`, data).then(r => r.data);
export const deleteStaff = (id) => api.delete(`/staff/${id}`).then(r => r.data);
// Contractors
export const getContractors = () => api.get('/contractors').then(r => r.data);
export const createContractor = (data) => api.post('/contractors', data).then(r => r.data);
export const updateContractor = (id, data) => api.put(`/contractors/${id}`, data).then(r => r.data);
export const deleteContractor = (id) => api.delete(`/contractors/${id}`).then(r => r.data);
// Forms
export const getForms = () => api.get('/forms').then(r => r.data);
export const createFormEntry = (data) => api.post('/forms', data).then(r => r.data);
export const updateFormEntry = (id, data) => api.put(`/forms/${id}`, data).then(r => r.data);
export const deleteFormEntry = (id) => api.delete(`/forms/${id}`).then(r => r.data);
// Sales Report — now accepts month too
export const getSalesReport = (year, month) => api.get('/sales-report', { params: { year, month } }).then(r => r.data);
// Auth
export const login = (data) => api.post('/auth/login', data).then(r => r.data);
export const changePassword = (data) => api.post('/auth/change-password', data).then(r => r.data);
export const getStaffReport = (month) => api.get('/staff-report', { params: month ? { month } : {} }).then(r => r.data);
// Attachments
export const getAttachments = (jobId) => api.get(`/attachments/${jobId}`).then(r => r.data);
export const uploadAttachments = (jobId, files) => {
  const fd = new FormData();
  files.forEach(f => fd.append('files', f));
  return api.post(`/attachments/${jobId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const deleteAttachment = (jobId, id) => api.delete(`/attachments/${jobId}/${id}`).then(r => r.data);
export const getAttachmentUrl = (jobId, filename) => `/api/attachments/${jobId}/file/${filename}`;

// ── Training / LMS ──
export const getCurriculum = () => api.get('/training/curriculum').then(r => r.data);
export const getLesson = (id) => api.get(`/training/lesson/${id}`).then(r => r.data);
export const completeLesson = (id) => api.post(`/training/lesson/${id}/complete`).then(r => r.data);
export const getQuiz = (id) => api.get(`/training/quiz/${id}`).then(r => r.data);
export const getPracticeQuestions = (lessonId) => api.get(`/training/lesson/${lessonId}/practice`).then(r => r.data);
export const submitQuiz = (id, answers) => api.post(`/training/quiz/${id}/submit`, { answers }).then(r => r.data);
export const getQuizStats = (id) => api.get(`/training/quiz/${id}/stats`).then(r => r.data);
export const getTrainingAudioUrl = (filename) => `/api/training/audio/${filename}`;
// admin
export const createLevel = (data) => api.post('/training/admin/level', data).then(r => r.data);
// ── Courses: a course groups a set of levels ──
export const getCourses = () => api.get('/training/courses').then(r => r.data);
// Who is actually completing the training
export const getTrainingProgress = (courseId) =>
  api.get('/training/admin/progress', { params: courseId ? { course_id: courseId } : {} }).then(r => r.data);
export const getRepAttempts = (userId) => api.get(`/training/admin/progress/${userId}`).then(r => r.data);
// Record that a rep opened a guide or played a video. Fire-and-forget:
// a failure here must never interrupt what the rep is doing.
export const logTrainingActivity = (kind, ref, label, seconds) =>
  api.post('/training/activity', { kind, ref, label, seconds }).catch(() => {});
export const createCourse = (d) => api.post('/training/admin/course', d).then(r => r.data);
export const updateCourse = (id, d) => api.put(`/training/admin/course/${id}`, d).then(r => r.data);
export const deleteCourse = (id, moveTo) =>
  api.delete(`/training/admin/course/${id}`, { params: moveTo ? { move_to: moveTo } : {} }).then(r => r.data);
export const setLevelCourse = (levelId, courseId) =>
  api.put(`/training/admin/level/${levelId}/course`, { course_id: courseId }).then(r => r.data);
export const updateLevel = (id, data) => api.put(`/training/admin/level/${id}`, data).then(r => r.data);
export const deleteLevel = (id) => api.delete(`/training/admin/level/${id}`).then(r => r.data);
export const createLesson = (fd) => api.post('/training/admin/lesson', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
export const updateLesson = (id, fd) => api.put(`/training/admin/lesson/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
export const deleteLesson = (id) => api.delete(`/training/admin/lesson/${id}`).then(r => r.data);
export const createQuiz = (data) => api.post('/training/admin/quiz', data).then(r => r.data);
export const deleteQuiz = (id) => api.delete(`/training/admin/quiz/${id}`).then(r => r.data);
export const resetAttempts = (user_id, quiz_id) => api.post('/training/admin/reset-attempts', { user_id, quiz_id }).then(r => r.data);
export const getTrainingOverview = () => api.get('/training/admin/overview').then(r => r.data);
export const seedTraining = () => api.post('/training/admin/seed').then(r => r.data);
export const importCprSteps = () => api.post('/training/admin/import-cpr-steps').then(r => r.data);
// video library
export const getVideoLibrary = () => api.get('/training/videos').then(r => r.data);
export const getTrainingVideoUrl = (filename) => `/api/training/video-file/${filename}`;
export const createVideoSection = (data) => api.post('/training/admin/video-section', data).then(r => r.data);
export const deleteVideoSection = (id) => api.delete(`/training/admin/video-section/${id}`).then(r => r.data);
export const uploadTrainingVideo = (fd, onProgress) => api.post('/training/admin/video', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: onProgress }).then(r => r.data);
export const deleteTrainingVideo = (id) => api.delete(`/training/admin/video/${id}`).then(r => r.data);
// sales presentations (Canva embeds)
export const getPresentations = () => api.get('/presentations').then(r => r.data);
export const addPresentation = (data) => api.post('/presentations', data).then(r => r.data);
export const updatePresentation = (id, d) => api.put(`/presentations/${id}`, d).then(r => r.data);
export const deletePresentation = (id) => api.delete(`/presentations/${id}`).then(r => r.data);

// My Dashboard (backend/routes/me.js) — one person's own numbers
export const getMyDashboard = (month, staff) => api.get('/me/dashboard', { params: { month, staff } }).then(r => r.data);
export const saveMyGoal = goal => api.put('/me/goal', { goal }).then(r => r.data);
export const getCeoDashboard = () => api.get('/ceo').then(r => r.data);
export const saveMyCommGoal = commGoal => api.put('/me/goal', { commGoal }).then(r => r.data);

export default api;

// Money In report — payments received in a date range (admins only)
// Appointments imported from Sales Gen (backend/routes/appointments.js)
export const getAppointments = (from, to) => api.get('/appointments', { params: { from, to } }).then(r => r.data);
export const importAppointments = (filename, data) => api.post('/appointments/import', { filename, data }).then(r => r.data);
// CPR Live appointments: book → dispatch → texts → results (backend/routes/leads.js)
export const getLeads = (from, to) => api.get('/leads', { params: { from, to } }).then(r => r.data);
export const getLead = id => api.get(`/leads/${id}`).then(r => r.data);
export const checkLeadDup = (phone, address, id) => api.get('/leads/check', { params: { phone, address, id } }).then(r => r.data);
export const createLead = d => api.post('/leads', d).then(r => r.data);
export const updateLead = (id, d) => api.put(`/leads/${id}`, d).then(r => r.data);
export const deleteLead = id => api.delete(`/leads/${id}`).then(r => r.data);
export const assignLeads = assignments => api.post('/leads/assign', { assignments }).then(r => r.data);
export const sendLeadTexts = (date, ids) => api.post('/leads/send', { date, ids }).then(r => r.data);
export const setLeadResult = (id, d) => api.put(`/leads/${id}/result`, d).then(r => r.data);
export const linkLeadJob = (id, job_id) => api.put(`/leads/${id}/job`, { job_id }).then(r => r.data);
export const getLeadMessages = date => api.get('/leads/messages', { params: { date } }).then(r => r.data);
export const saveCrew = c => (c.id ? api.put(`/leads/crews/${c.id}`, c) : api.post('/leads/crews', c)).then(r => r.data);
export const saveRepPhone = (staffId, phone) => api.put(`/leads/rep-phone/${staffId}`, { phone }).then(r => r.data);
export const getVisit = token => api.get(`/visit/${token}`).then(r => r.data);
export const saveVisit = (token, d) => api.post(`/visit/${token}`, d).then(r => r.data);
export const saveApptPerson = (sg_name, staff_id, kind) => api.put('/appointments/people', { sg_name, staff_id, kind }).then(r => r.data);
// Staff profiles (backend/routes/profiles.js)
export const getProfiles = () => api.get('/profiles').then(r => r.data);
export const updateProfile = (id, data) => api.put(`/profiles/${id}`, data).then(r => r.data);
export const uploadProfilePhoto = (id, image) => api.post(`/profiles/${id}/photo`, { image }).then(r => r.data);
export const getMyProfile = () => api.get('/profiles/me').then(r => r.data);
export const updateMyProfile = (data) => api.put('/profiles/me', data).then(r => r.data);
export const uploadMyPhoto = (image) => api.post('/profiles/me/photo', { image }).then(r => r.data);
// University tests an admin unlocks per person (backend/routes/assessments.js)
export const getMyTests = () => api.get('/assessments/mine').then(r => r.data);
export const getTestToTake = (key) => api.get(`/assessments/${key}/take`).then(r => r.data);
export const submitTest = (key, body) => api.post(`/assessments/${key}/submit`, body).then(r => r.data);
export const getMyExamReview = () => api.get('/assessments/field-rep-exam/review').then(r => r.data);
export const getTestPeople = () => api.get('/assessments/admin/people').then(r => r.data);
export const setTestUnlock = (key, body) => api.post('/assessments/admin/unlock', { key, ...body }).then(r => r.data);
export const giveExtraTry = (key, user_id) => api.post('/assessments/admin/extra-try', { key, user_id }).then(r => r.data);
export const getTestResult = (id) => api.get(`/assessments/admin/result/${id}`).then(r => r.data);
// Reports worked out from the records (backend/routes/reports.js)
export const getPaceReport = () => api.get('/reports/pace').then(r => r.data);
export const getLeaderboard = (from, to) => api.get('/reports/leaderboard', { params: { from, to } }).then(r => r.data);
export const getSalesRange = (from, to) => api.get('/reports/sales', { params: { from, to } }).then(r => r.data);
export const getToCollect = () => api.get('/reports/collect').then(r => r.data);
export const getMoneyIn = (from, to) => api.get('/money-in', { params: { from, to } }).then(r => r.data);
// Payment Sheet — one row per job: deposits, collections, financing, remaining (admins only)
export const getPaymentSheet = (status) => api.get('/payment-sheet', { params: { status } }).then(r => r.data);
